import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/Skeletons';

interface HostingSite {
  domain: string;
  client_name: string;
  docroot: string | null;
  status: 'active' | 'suspended';
  notes: string | null;
  created_at: string;
  price_eur: number | null;
  billed_since: string | null;
  url: string;
}

const KEY = ['hosting'];

export function Hosting(): JSX.Element {
  const qc = useQueryClient();
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: KEY });
    void qc.invalidateQueries({ queryKey: ['billing'] });
  };

  const { data, isLoading } = useQuery({
    queryKey: KEY,
    queryFn: () => api.get<{ sites: HostingSite[] }>('/admin/hosting'),
  });

  const [addOpen, setAddOpen] = useState(false);
  const [priceFor, setPriceFor] = useState<HostingSite | null>(null);

  const createMutation = useMutation({
    mutationFn: (v: { domain: string; client_name: string }) => api.post('/admin/hosting', v),
    onSuccess: () => {
      setAddOpen(false);
      invalidate();
    },
  });

  const statusMutation = useMutation({
    mutationFn: (v: { domain: string; status: 'active' | 'suspended' }) =>
      api.patch(`/admin/hosting/${encodeURIComponent(v.domain)}`, { status: v.status }),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (domain: string) => api.delete(`/admin/hosting/${encodeURIComponent(domain)}`),
    onSuccess: invalidate,
  });

  const priceMutation = useMutation({
    mutationFn: (v: { domain: string; price_eur: number | null; billed_since: string | null }) =>
      api.put(`/admin/connections/hosting/${encodeURIComponent(v.domain)}/price`, {
        price_eur: v.price_eur,
        billed_since: v.billed_since,
        label: `Хостинг: ${v.domain}`,
        client_name: priceFor?.client_name ?? '',
      }),
    onSuccess: () => {
      setPriceFor(null);
      invalidate();
    },
  });

  const sites = data?.sites ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Хостинг</h1>
          <p className="text-sm text-muted-foreground">
            Статические сайты клиентов на нашем VPS. Тариф и дата начисления учитываются в разделе
            «Биллинг» как связка клиента.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Добавить сайт
        </Button>
      </div>

      {isLoading ? (
        <TableSkeleton columns={['Домен', 'Клиент', 'Статус', 'Тариф €/мес', 'Начисление с', 'Действия']} />
      ) : sites.length === 0 ? (
        <p className="text-sm text-muted-foreground">Пока нет сайтов.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Домен</TableHead>
              <TableHead>Клиент</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead className="text-right">Тариф €/мес</TableHead>
              <TableHead>Начисление с</TableHead>
              <TableHead className="text-right">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sites.map((s) => (
              <TableRow key={s.domain}>
                <TableCell>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                  >
                    {s.domain}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </TableCell>
                <TableCell>{s.client_name}</TableCell>
                <TableCell>
                  <span
                    className={
                      s.status === 'active'
                        ? 'rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                        : 'rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                    }
                  >
                    {s.status === 'active' ? 'активен' : 'приостановлен'}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {s.price_eur !== null ? `€${s.price_eur.toFixed(0)}` : '—'}
                </TableCell>
                <TableCell className="tabular-nums text-muted-foreground">
                  {s.billed_since ?? '—'}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setPriceFor(s)} title="Тариф">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        statusMutation.mutate({
                          domain: s.domain,
                          status: s.status === 'active' ? 'suspended' : 'active',
                        })
                      }
                    >
                      {s.status === 'active' ? 'Пауза' : 'Включить'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Удалить ${s.domain} из хостинга? Файлы на VPS останутся.`)) {
                          deleteMutation.mutate(s.domain);
                        }
                      }}
                      title="Удалить"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AddSiteDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSubmit={(v) => createMutation.mutate(v)}
        pending={createMutation.isPending}
        error={createMutation.error instanceof Error ? createMutation.error.message : null}
      />

      <PriceDialog
        site={priceFor}
        onOpenChange={(o) => !o && setPriceFor(null)}
        onSubmit={(v) => priceFor && priceMutation.mutate({ domain: priceFor.domain, ...v })}
        pending={priceMutation.isPending}
      />
    </div>
  );
}

function AddSiteDialog(props: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSubmit: (v: { domain: string; client_name: string }) => void;
  pending: boolean;
  error: string | null;
}): JSX.Element {
  const [domain, setDomain] = useState('');
  const [client, setClient] = useState('');
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Добавить сайт на хостинг</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="h-domain">Домен</Label>
            <Input
              id="h-domain"
              placeholder="example.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="h-client">Клиент</Label>
            <Input
              id="h-client"
              placeholder="Eurocycle"
              value={client}
              onChange={(e) => setClient(e.target.value)}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Заводит запись для учёта. Сам сайт на VPS разворачивается скриптами Paradigma Hosting
            (docroot /var/www/hosting/&lt;домен&gt;/public). Тариф задаётся кнопкой ✎ после
            добавления.
          </p>
          {props.error && <p className="text-sm text-destructive">{props.error}</p>}
        </div>
        <DialogFooter>
          <Button
            disabled={props.pending || !domain.trim() || !client.trim()}
            onClick={() => props.onSubmit({ domain: domain.trim(), client_name: client.trim() })}
          >
            Добавить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PriceDialog(props: {
  site: HostingSite | null;
  onOpenChange: (o: boolean) => void;
  onSubmit: (v: { price_eur: number | null; billed_since: string | null }) => void;
  pending: boolean;
}): JSX.Element {
  const [price, setPrice] = useState('');
  const [since, setSince] = useState('');
  // Синхронизируем поля с выбранным сайтом при открытии.
  const key = props.site?.domain ?? '';
  const [lastKey, setLastKey] = useState('');
  if (key !== lastKey) {
    setLastKey(key);
    setPrice(props.site?.price_eur != null ? String(props.site.price_eur) : '');
    setSince(props.site?.billed_since ?? '');
  }
  return (
    <Dialog open={props.site !== null} onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Тариф хостинга — {props.site?.domain}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="h-price">Тариф, €/мес</Label>
            <Input
              id="h-price"
              type="number"
              min="0"
              step="1"
              placeholder="например, 15"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="h-since">Начислять с даты</Label>
            <Input
              id="h-since"
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Пусто в обоих полях — начисление снимается. Долг считается в разделе «Биллинг».
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            disabled={props.pending}
            onClick={() =>
              props.onSubmit({
                price_eur: price.trim() === '' ? null : Number(price),
                billed_since: since.trim() === '' ? null : since,
              })
            }
          >
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
